import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { loginCoMember, ApiError } from "../../services/api";
import { useAuth } from "../../services/auth-context";
import { LanguageSwitcher } from "../../components/LanguageSwitcher";
import { PasswordInput } from "../../components/PasswordInput";

export function CoMemberLoginPage() {
  const { t } = useTranslation();
  const { setAuth } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryAfterSeconds, setRetryAfterSeconds] = useState<number | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setRetryAfterSeconds(null);
    try {
      await loginCoMember(email, password, rememberMe);
      setAuth({ role: "co_member" });
      navigate("/co-member", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        const body = err.body as { retryAfterSeconds?: number } | null;
        setRetryAfterSeconds(body?.retryAfterSeconds ?? null);
        setError(t("auth.tooManyAttempts", "Too many attempts. Please wait before trying again."));
      } else if (err instanceof ApiError && err.status === 401) {
        setError(t("auth.wrongPassword", "Incorrect email or password."));
      } else {
        setError(t("auth.genericError", "Something went wrong. Please try again."));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex justify-end p-2">
        <LanguageSwitcher />
      </div>
      <div className="flex flex-1 items-center justify-center bg-surface-alt">
        <form
          onSubmit={onSubmit}
          className="flex w-80 flex-col gap-4 rounded-lg bg-surface p-8 shadow"
        >
          <img src="/branding/logo-full.png" alt="Base.vn" className="mx-auto h-10" />
          <h1 className="text-center font-sans text-lg text-ink">
            {t("auth.coMemberSignInTitle", "CO Member Sign In")}
          </h1>

          <label className="flex flex-col gap-1 text-sm text-body">
            {t("common.email", "Email")}
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded border border-surface-alt px-3 py-2"
            />
          </label>

          <PasswordInput
            label={t("common.password", "Password")}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {/* FR-087: opt-in, so an unticked box keeps the short session — a shared computer
              is never left signed in by default. */}
          <label className="flex items-center gap-2 text-sm text-body">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            {t("auth.rememberMe", "Remember me")}
          </label>

          {error && (
            <p className="text-sm text-accent">
              {error}
              {retryAfterSeconds !== null && ` (~${Math.ceil(retryAfterSeconds / 60)} min)`}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="rounded bg-brand px-3 py-2 font-sans text-surface disabled:opacity-50"
          >
            {submitting ? t("common.signingIn", "Signing in…") : t("common.signIn", "Sign In")}
          </button>
        </form>
      </div>
    </div>
  );
}
